

import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { Sample, AnalysisCatalogItem } from '../../types';
import { TestTube, Layers } from 'lucide-react';

interface AnalysisRequestModalProps {
  sample: Sample;
  catalogItems: AnalysisCatalogItem[];
  onClose: () => void;
  onConfirm: () => void;
}

const AnalysisRequestModal: React.FC<AnalysisRequestModalProps> = ({ sample, catalogItems, onClose, onConfirm }) => {
  const [selectedAnalyses, setSelectedAnalyses] = useState<string[]>([]);

  useEffect(() => {
    if (sample.analyses) {
        setSelectedAnalyses(sample.analyses);
    }
  }, [sample]);

  const toggleAnalysisSelection = (item: AnalysisCatalogItem) => {
    const isSelected = selectedAnalyses.includes(item.name);

    if (item.sub_analyses && item.sub_analyses.length > 0) {
        // PANEL LOGIC: Select/Deselect all children
        const children = catalogItems.filter(i => item.sub_analyses?.includes(i.id));
        const childrenNames = children.map(c => c.name);

        if (isSelected) {
            // Deselect all children that are currently selected
            setSelectedAnalyses(prev => prev.filter(name => !childrenNames.includes(name)));
        } else {
            // Add all children
            // We use Set to avoid duplicates
            setSelectedAnalyses(prev => Array.from(new Set([...prev, ...childrenNames])));
        }
    } else {
        // NORMAL LOGIC
        if (isSelected) {
            setSelectedAnalyses(selectedAnalyses.filter(a => a !== item.name));
        } else {
            setSelectedAnalyses([...selectedAnalyses, item.name]);
        }
    }
  };

  const handleConfirm = async () => {
    if (selectedAnalyses.length === 0) {
        alert("Veuillez sélectionner au moins une analyse.");
        return;
    }

    const newStatus = 'processing';
    
    if (isSupabaseConfigured()) {
        const { error } = await supabase.from('samples')
          .update({ status: newStatus, analyses: selectedAnalyses })
          .eq('id', sample.id);
        
        if (!error) onConfirm();
    } else {
        // Mock update handled by parent refresh usually, but here we assume success
        onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
                <h3 className="font-bold text-lg text-slate-800">Demande d'Analyse</h3>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-600">&times;</button>
            </div>
            <div className="p-6">
                <p className="text-sm text-slate-600 mb-4">
                    Examens à réaliser pour <span className="font-bold">{sample.patients?.last_name}</span>.
                </p>
                <div className="space-y-2 mb-6 max-h-60 overflow-y-auto">
                    {catalogItems.length > 0 ? (
                        catalogItems
                        .filter(item => item.active !== false) // Only show active items
                        .map(item => {
                            // Check if it's a panel
                            const isPanel = item.sub_analyses && item.sub_analyses.length > 0;
                            // For a panel, check if ALL children are selected to consider the panel "checked" visually
                            // Or simpler: The panel button is an action button
                            // Let's stick to checkbox logic for consistency, but if it's a panel, we check if children are in
                            let isChecked = false;
                            
                            if (isPanel) {
                                const childrenIDs = item.sub_analyses || [];
                                const childrenNames = catalogItems.filter(c => childrenIDs.includes(c.id)).map(c => c.name);
                                // If at least one child is selected, we could show partial, but here we check if ALL are selected for full check
                                // Simplification: If all children are selected, it's checked.
                                isChecked = childrenNames.length > 0 && childrenNames.every(name => selectedAnalyses.includes(name));
                            } else {
                                isChecked = selectedAnalyses.includes(item.name);
                            }

                            return (
                                <label key={item.id} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${isPanel ? 'bg-purple-50 border-purple-100 hover:bg-purple-100' : 'border-slate-200 hover:bg-slate-50'}`}>
                                    <input 
                                      type="checkbox" 
                                      checked={isChecked}
                                      onChange={() => toggleAnalysisSelection(item)}
                                      className={`w-5 h-5 rounded focus:ring-opacity-50 ${isPanel ? 'text-purple-600 focus:ring-purple-500' : 'text-indigo-600 focus:ring-indigo-500'}`}
                                    />
                                    <div className="flex-1">
                                        <div className={`font-medium ${isPanel ? 'text-purple-900' : 'text-slate-800'} flex items-center justify-between`}>
                                            {item.name}
                                            {isPanel && <Layers size={14} className="text-purple-400"/>}
                                        </div>
                                        {isPanel && (
                                            <div className="text-xs text-purple-600 mt-0.5">
                                                Panel : Ajoute {item.sub_analyses?.length} analyses
                                            </div>
                                        )}
                                    </div>
                                </label>
                            );
                        })
                    ) : (
                        <div className="text-center text-slate-400">Catalogue vide.</div>
                    )}
                </div>
                <div className="flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium">Annuler</button>
                    <button onClick={handleConfirm} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 shadow-sm flex items-center gap-2">
                        <TestTube size={16} /> Lancer l'analyse
                    </button>
                </div>
            </div>
        </div>
    </div>
  );
};

export default AnalysisRequestModal;